/**
 * Image Gallery Lightbox Component
 * Opens a full-screen gallery with thumbnails when images are clicked
 */

if (!customElements.get('image-gallery-lightbox')) {
  class ImageGalleryLightbox extends HTMLElement {
    constructor() {
      super();
      this.currentIndex = 0;
      /** @type {Array<{src: string, srcLarge: string, alt: string, thumbnail: string}>} */
      this.images = [];
      this.isOpen = false;
      this.touchStartX = 0;
      this.touchEndX = 0;
      
      // DOM element references
      /** @type {HTMLElement|null} */
      this.overlay = null;
      /** @type {HTMLElement|null} */
      this.backdrop = null;
      /** @type {HTMLElement|null} */
      this.closeBtn = null;
      /** @type {HTMLElement|null} */
      this.prevBtn = null;
      /** @type {HTMLElement|null} */
      this.nextBtn = null;
      /** @type {HTMLImageElement|null} */
      this.mainImage = null;
      /** @type {HTMLElement|null} */
      this.imageContainer = null;
      /** @type {HTMLElement|null} */
      this.loader = null;
      /** @type {NodeListOf<HTMLElement>|null} */
      this.thumbnails = null;
      /** @type {HTMLElement|null} */
      this.thumbnailsWrapper = null;
      /** @type {HTMLElement|null} */
      this.counter = null;
      /** @type {HTMLImageElement|null} */
      this.secondaryImage = null;
      /** @type {HTMLElement|null} */
      this.secondaryWrapper = null;
      /** @type {HTMLElement|null} */
      this.counterSecondary = null;
      /** @type {HTMLElement|null} */
      this.lightboxContainer = null;
    }

    connectedCallback() {
      this.galleryId = this.dataset.galleryId;
      this.collectImages();
      this.render();
      this.bindEvents();
    }

    collectImages() {
      const wrapper = document.querySelector(`[data-image-gallery-id="${this.galleryId}"]`);
      if (!wrapper) return;

      const imageElements = wrapper.querySelectorAll('.gallery-item img');
      this.images = Array.from(imageElements).map((img, index) => {
        const imgEl = /** @type {HTMLImageElement} */ (img);
        return {
          src: imgEl.src.replace(/width=\d+/, 'width=1600'),
          srcLarge: imgEl.src.replace(/width=\d+/, 'width=2000'),
          alt: imgEl.alt || `Gallery image ${index + 1}`,
          thumbnail: imgEl.src.replace(/width=\d+/, 'width=200')
        };
      });
    }

    render() {
      // Create container and inject into body to avoid z-index stacking issues
      this.lightboxContainer = document.createElement('div');
      this.lightboxContainer.className = 'image-gallery-lightbox-container';
      this.lightboxContainer.dataset.galleryId = this.galleryId || '';
      
      this.lightboxContainer.innerHTML = `
        <div class="lightbox-overlay" aria-hidden="true" role="dialog" aria-modal="true" aria-label="Image gallery lightbox">
          <div class="lightbox-backdrop"></div>
          
          <button class="lightbox-close" aria-label="Close gallery">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor" width="28" height="28">
              <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>

          <div class="lightbox-content">
            <div class="lightbox-main">
              <button class="lightbox-nav lightbox-prev" aria-label="Previous image">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor" width="32" height="32">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" />
                </svg>
              </button>

              <div class="lightbox-image-container">
                <div class="lightbox-image-wrapper primary">
                  <img class="lightbox-image lightbox-image-primary" src="" alt="" loading="eager" />
                </div>
                <div class="lightbox-image-wrapper secondary">
                  <img class="lightbox-image lightbox-image-secondary" src="" alt="" loading="eager" />
                </div>
                <div class="lightbox-loader">
                  <div class="lightbox-spinner"></div>
                </div>
              </div>

              <button class="lightbox-nav lightbox-next" aria-label="Next image">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor" width="32" height="32">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
                </svg>
              </button>
            </div>

            <div class="lightbox-thumbnails-wrapper">
              <div class="lightbox-thumbnails">
                ${this.images.map((img, i) => `
                  <button class="lightbox-thumb${i === 0 ? ' active' : ''}" data-index="${i}" aria-label="View image ${i + 1}">
                    <img src="${img.thumbnail}" alt="${img.alt}" loading="lazy" />
                  </button>
                `).join('')}
              </div>
            </div>

            <div class="lightbox-counter">
              <span class="lightbox-current">1</span><span class="lightbox-current-secondary"></span> / <span class="lightbox-total">${this.images.length}</span>
            </div>
          </div>
        </div>
      `;

      // Append to body to avoid z-index stacking context issues
      document.body.appendChild(this.lightboxContainer);

      // Cache DOM elements (now query from lightboxContainer instead of this)
      this.overlay = this.lightboxContainer.querySelector('.lightbox-overlay');
      this.backdrop = this.lightboxContainer.querySelector('.lightbox-backdrop');
      this.closeBtn = this.lightboxContainer.querySelector('.lightbox-close');
      this.prevBtn = this.lightboxContainer.querySelector('.lightbox-prev');
      this.nextBtn = this.lightboxContainer.querySelector('.lightbox-next');
      this.mainImage = /** @type {HTMLImageElement|null} */ (this.lightboxContainer.querySelector('.lightbox-image-primary'));
      this.secondaryImage = /** @type {HTMLImageElement|null} */ (this.lightboxContainer.querySelector('.lightbox-image-secondary'));
      this.secondaryWrapper = this.lightboxContainer.querySelector('.lightbox-image-wrapper.secondary');
      this.imageContainer = this.lightboxContainer.querySelector('.lightbox-image-container');
      this.loader = this.lightboxContainer.querySelector('.lightbox-loader');
      this.thumbnails = this.lightboxContainer.querySelectorAll('.lightbox-thumb');
      this.thumbnailsWrapper = this.lightboxContainer.querySelector('.lightbox-thumbnails');
      this.counter = this.lightboxContainer.querySelector('.lightbox-current');
      this.counterSecondary = this.lightboxContainer.querySelector('.lightbox-current-secondary');
    }

    bindEvents() {
      // Bind gallery image clicks
      const wrapper = document.querySelector(`[data-image-gallery-id="${this.galleryId}"]`);
      if (!wrapper) return;

      const galleryItems = wrapper.querySelectorAll('.gallery-item');
      galleryItems.forEach((item, index) => {
        // Only make clickable if not already a link
        if (!item.closest('a')) {
          const htmlItem = /** @type {HTMLElement} */ (item);
          htmlItem.style.cursor = 'pointer';
          htmlItem.addEventListener('click', (e) => {
            e.preventDefault();
            this.open(index);
          });
        }
      });

      // Close button
      if (this.closeBtn) {
        this.closeBtn.addEventListener('click', () => this.close());
      }
      
      // Backdrop click
      if (this.backdrop) {
        this.backdrop.addEventListener('click', () => this.close());
      }

      // Navigation buttons
      if (this.prevBtn) {
        this.prevBtn.addEventListener('click', () => this.prev());
      }
      if (this.nextBtn) {
        this.nextBtn.addEventListener('click', () => this.next());
      }

      // Thumbnail clicks
      if (this.thumbnails) {
        this.thumbnails.forEach(thumb => {
          thumb.addEventListener('click', () => {
            const index = parseInt(/** @type {HTMLElement} */ (thumb).dataset.index || '0', 10);
            this.goTo(index);
          });
        });
      }

      // Keyboard navigation
      this.boundKeydownHandler = /** @param {KeyboardEvent} e */ (e) => this.handleKeydown(e);
      document.addEventListener('keydown', this.boundKeydownHandler);

      // Touch/swipe support
      if (this.imageContainer) {
        this.imageContainer.addEventListener('touchstart', (e) => this.handleTouchStart(e), { passive: true });
        this.imageContainer.addEventListener('touchend', (e) => this.handleTouchEnd(e), { passive: true });
      }

      // Image load handling
      if (this.mainImage) {
        this.mainImage.addEventListener('load', () => this.handleImageLoad('primary'));
      }
      if (this.secondaryImage) {
        this.secondaryImage.addEventListener('load', () => this.handleImageLoad('secondary'));
      }
    }

    /**
     * @param {KeyboardEvent} e
     */
    handleKeydown(e) {
      if (!this.isOpen) return;

      switch (e.key) {
        case 'Escape':
          this.close();
          break;
        case 'ArrowLeft':
          this.prev();
          break;
        case 'ArrowRight':
          this.next();
          break;
      }
    }

    /**
     * @param {TouchEvent} e
     */
    handleTouchStart(e) {
      if (e.changedTouches && e.changedTouches[0]) {
        this.touchStartX = e.changedTouches[0].screenX;
      }
    }

    /**
     * @param {TouchEvent} e
     */
    handleTouchEnd(e) {
      if (e.changedTouches && e.changedTouches[0]) {
        this.touchEndX = e.changedTouches[0].screenX;
      }
      this.handleSwipe();
    }

    handleSwipe() {
      const swipeThreshold = 50;
      const diff = this.touchStartX - this.touchEndX;

      if (Math.abs(diff) > swipeThreshold) {
        if (diff > 0) {
          this.next();
        } else {
          this.prev();
        }
      }
    }

    /**
     * @param {'primary'|'secondary'} which
     */
    handleImageLoad(which) {
      // Hide loader when primary image loads
      if (which === 'primary' && this.loader) {
        this.loader.classList.remove('active');
      }
      if (which === 'primary' && this.mainImage) {
        this.mainImage.classList.add('loaded');
      }
      if (which === 'secondary' && this.secondaryImage) {
        this.secondaryImage.classList.add('loaded');
      }
    }

    /**
     * @param {number} index
     */
    open(index = 0) {
      this.isOpen = true;
      this.currentIndex = index;
      this.updateImage(null);
      
      // Prevent body scroll
      document.body.style.overflow = 'hidden';
      
      // Show overlay with animation
      if (this.overlay) {
        this.overlay.classList.add('active');
        this.overlay.setAttribute('aria-hidden', 'false');
      }
      
      // Focus trap
      if (this.closeBtn) {
        /** @type {HTMLButtonElement} */ (this.closeBtn).focus();
      }
    }

    close() {
      this.isOpen = false;
      
      // Restore body scroll
      document.body.style.overflow = '';
      
      // Hide overlay with animation
      if (this.overlay) {
        this.overlay.classList.remove('active');
        this.overlay.setAttribute('aria-hidden', 'true');
      }
    }

    prev() {
      if (this.images.length <= 1) return;
      this.currentIndex = (this.currentIndex - 1 + this.images.length) % this.images.length;
      this.updateImage('prev');
    }

    next() {
      if (this.images.length <= 1) return;
      this.currentIndex = (this.currentIndex + 1) % this.images.length;
      this.updateImage('next');
    }

    /**
     * @param {number} index
     */
    goTo(index) {
      if (index === this.currentIndex) return;
      const direction = index > this.currentIndex ? 'next' : 'prev';
      this.currentIndex = index;
      this.updateImage(direction);
    }

    /**
     * Check if we're in desktop mode (2 images visible)
     */
    isDesktopMode() {
      return window.innerWidth >= 1024;
    }

    /**
     * @param {'prev'|'next'|null} direction
     */
    updateImage(direction) {
      const image = this.images[this.currentIndex];
      if (!image) return;

      const isDesktop = this.isDesktopMode();
      const secondaryIndex = this.currentIndex + 1;
      const hasSecondaryImage = isDesktop && secondaryIndex < this.images.length;
      const secondaryImage = hasSecondaryImage ? this.images[secondaryIndex] : null;

      // Show loader
      if (this.loader) this.loader.classList.add('active');
      if (this.mainImage) this.mainImage.classList.remove('loaded');
      if (this.secondaryImage) this.secondaryImage.classList.remove('loaded');

      // Add slide animation
      if (direction && this.mainImage) {
        this.mainImage.classList.remove('slide-in-left', 'slide-in-right');
        this.mainImage.classList.add(direction === 'next' ? 'slide-in-right' : 'slide-in-left');
      }
      if (direction && this.secondaryImage) {
        this.secondaryImage.classList.remove('slide-in-left', 'slide-in-right');
        this.secondaryImage.classList.add(direction === 'next' ? 'slide-in-right' : 'slide-in-left');
      }

      // Update primary image
      if (this.mainImage) {
        this.mainImage.src = image.srcLarge;
        this.mainImage.alt = image.alt;
      }

      // Update secondary image (desktop only)
      if (this.secondaryImage && this.secondaryWrapper) {
        if (hasSecondaryImage && secondaryImage) {
          this.secondaryWrapper.style.display = '';
          this.secondaryImage.src = secondaryImage.srcLarge;
          this.secondaryImage.alt = secondaryImage.alt;
        } else {
          this.secondaryWrapper.style.display = 'none';
        }
      }

      // Update counter
      if (this.counter) {
        this.counter.textContent = String(this.currentIndex + 1);
      }
      if (this.counterSecondary) {
        if (hasSecondaryImage) {
          this.counterSecondary.textContent = `-${secondaryIndex + 1}`;
        } else {
          this.counterSecondary.textContent = '';
        }
      }

      // Update active thumbnail(s)
      if (this.thumbnails) {
        this.thumbnails.forEach((thumb, i) => {
          const isActive = i === this.currentIndex || (hasSecondaryImage && i === secondaryIndex);
          thumb.classList.toggle('active', isActive);
        });
      }

      // Scroll thumbnail into view
      if (this.thumbnails) {
        const activeThumb = this.thumbnails[this.currentIndex];
        if (activeThumb && this.thumbnailsWrapper) {
          activeThumb.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
        }
      }
    }
    
    disconnectedCallback() {
      // Cleanup event listeners
      if (this.boundKeydownHandler) {
        document.removeEventListener('keydown', this.boundKeydownHandler);
      }
      
      // Remove the lightbox container from body
      if (this.lightboxContainer && this.lightboxContainer.parentNode) {
        this.lightboxContainer.parentNode.removeChild(this.lightboxContainer);
      }
    }
  }

  customElements.define('image-gallery-lightbox', ImageGalleryLightbox);
}
